import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Loader from 'components/v2/common/loader';
import TitleTooltip from 'components/v2/common/titleTooltip';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type { DocumentTemplateRecord, ServiceRecord } from 'types/servicesDocuments';
import { localizedTemplateName } from 'utils/servicesDocuments';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface Props {
    open: boolean;
    service: ServiceRecord | null;
    onClose: () => void;
    onSaved: () => void | Promise<void>;
    language?: string;
}

const ServiceChecklistDialog = ({ open, service, onClose, onSaved, language }: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [automaticTemplates, setAutomaticTemplates] = useState<DocumentTemplateRecord[]>([]);
    const [selectableTemplates, setSelectableTemplates] = useState<DocumentTemplateRecord[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    useEffect(() => {
        if (!open || !service?.id) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const [checklistResponse, templatesResponse] = await Promise.all([
                    api.services.getServiceDocumentChecklist(service.id),
                    api.documentTemplates.listDocumentTemplates({
                        scope: service.service_provider_id == null ? 'universal' : 'visible',
                        country_code: service.country_code,
                        document_type: 'SERVICES_SELECTED',
                        ...(service.service_provider_id != null
                            ? { service_provider_id: service.service_provider_id }
                            : {}),
                    }),
                ]);
                if (cancelled) {
                    return;
                }
                const checklist = checklistResponse.data;
                setAutomaticTemplates(checklist.automatic_templates || []);
                const selectedSet = new Set(
                    (checklist.selected_templates || []).map((template) => template.id)
                );
                setSelectedIds(Array.from(selectedSet));

                setSelectableTemplates(templatesResponse.data || []);
            } catch (error) {
                if (!cancelled) {
                    dispatch(
                        setAlert({
                            message: getErrorMessage(
                                error,
                                translate('servicesDocuments.checklist.loadFailed')
                            ),
                            type: 'error',
                        })
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [
        dispatch,
        open,
        service?.country_code,
        service?.id,
        service?.service_provider_id,
        translate,
    ]);

    const sortedSelectable = useMemo(
        () =>
            [...selectableTemplates].sort((a, b) =>
                localizedTemplateName(a, language).localeCompare(localizedTemplateName(b, language))
            ),
        [language, selectableTemplates]
    );

    const toggleTemplate = (templateId: number) => {
        setSelectedIds((prev) =>
            prev.includes(templateId)
                ? prev.filter((id) => id !== templateId)
                : [...prev, templateId]
        );
    };

    const handleSave = async () => {
        if (!service?.id) {
            return;
        }
        try {
            setSaving(true);
            await api.services.putServiceDocumentChecklist(service.id, {
                document_template_ids: selectedIds,
            });
            dispatch(setAlert({ message: translate('saveChanges'), type: 'success' }));
            await onSaved();
            onClose();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        error,
                        translate('servicesDocuments.checklist.saveFailed')
                    ),
                    type: 'error',
                })
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={fullScreen}
            aria-labelledby="service-checklist-dialog-title"
        >
            <DialogTitle id="service-checklist-dialog-title">
                <TitleTooltip title={translate('servicesDocuments.checklist.title')} />
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Loader />
                ) : (
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {translate('servicesDocuments.checklist.automaticSection')}
                            </Typography>
                            {automaticTemplates.length ? (
                                <Stack spacing={0.5}>
                                    {automaticTemplates.map((template) => (
                                        <Typography key={template.id} variant="body2">
                                            {localizedTemplateName(template, language)}
                                        </Typography>
                                    ))}
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {translate('servicesDocuments.checklist.noAutomatic')}
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                {translate('servicesDocuments.checklist.selectedSection')}
                            </Typography>
                            <FormGroup data-testid="service-checklist-checkboxes">
                                {sortedSelectable.map((template) => (
                                    <FormControlLabel
                                        key={template.id}
                                        control={
                                            <Checkbox
                                                checked={selectedIds.includes(template.id)}
                                                onChange={() => toggleTemplate(template.id)}
                                            />
                                        }
                                        label={localizedTemplateName(template, language)}
                                    />
                                ))}
                            </FormGroup>
                        </Box>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" disabled={saving}>
                    {translate('cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => void handleSave()}
                    disabled={loading || saving}
                    data-testid="service-checklist-save"
                >
                    {translate('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ServiceChecklistDialog;
